import { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { getAgentMe, getCurrentCall, submitDisposition } from "../api/agent";
import type { CurrentCall } from "../api/agent";
import { listDispositions } from "../api/lookup";
import { listAgentDispositions } from "../api/agent";
import { getWarmTransfer } from "../api/warmTransfer";
import { AgentConsole } from "../components/AgentConsole";
import { ConnectionTest } from "../components/ConnectionTest";
import { LeadCapturePanel, flushLeadDrafts } from "../components/LeadCapturePanel";
import type { Softphone, PhoneState } from "../services/softphone";

export function AgentPanelPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAgent = user?.principal === "agent";

  const [operatorExtension, setOperatorExtension] = useState<number | null>(null);
  const [extInput, setExtInput] = useState("");
  const [lastDisposed, setLastDisposed] = useState<string | null>(null);

  // Threaded down to ConnectionTest so the self-test can reuse the console's
  // OWN already-registered softphone instead of registering a second one
  // under the same static SIP secret (see AgentConsole's onPhoneChange doc
  // comment, and connectionTest.ts's runEchoCallOnLiveSoftphone).
  const [liveSoftphone, setLiveSoftphone] = useState<Softphone | null>(null);
  const [livePhoneState, setLivePhoneState] = useState<PhoneState>("offline");
  // beads F4: threaded straight through to ConnectionTest as `livePhoneStateSeq`
  // — see AgentConsole's own `stateSeq` doc comment. Storing it as its own
  // piece of state (not derived) is what actually matters here: a
  // `setLivePhoneState(state)` call with the SAME string this state already
  // held would otherwise bail out with no re-render at all, never reaching
  // ConnectionTest with a fresh prop in the first place.
  const [livePhoneStateSeq, setLivePhoneStateSeq] = useState(0);
  const handlePhoneChange = useCallback((phone: Softphone, state: PhoneState, seq: number) => {
    setLiveSoftphone(phone);
    setLivePhoneState(state);
    setLivePhoneStateSeq(seq);
  }, []);

  // Threaded from ConnectionTest back down to AgentConsole so the console
  // can label an on-screen call as a self-test rather than "Unknown caller"
  // (see AgentConsole's `testCallActive` prop doc comment).
  const [testCallActive, setTestCallActive] = useState(false);

  // An agent's seat is baked into their token, not typed into a box — the
  // panel's source of truth for "which extension am I" is GET /api/agent/me
  // (Task 3.4). Skipped entirely for an operator, who has no single agent_me
  // to ask and keeps the extension-entry flow below (backward compatibility:
  // this is the shared-login path operators use today).
  const { data: me } = useQuery({
    queryKey: ["agent-me"],
    queryFn: getAgentMe,
    enabled: isAgent,
  });

  const extension = isAgent ? (me?.extension_id ?? null) : operatorExtension;

  // An agent login cannot read /lookup/dispositions — it is operator-gated and
  // 403s every AgentPrincipal, which rendered an empty wrap-up grid for every
  // agent on every call (beads pz2z). Agents get the agent-reachable list.
  const { data: dispositions, isError: dispositionsFailed } = useQuery({
    queryKey: ["dispositions", isAgent ? "agent" : "operator"],
    queryFn: isAgent ? listAgentDispositions : listDispositions,
  });

  // Poll the agent's current call every 2s — this drives the auto-pop.
  const { data: call } = useQuery({
    queryKey: ["agent-current-call", extension],
    queryFn: () => getCurrentCall(extension!),
    enabled: extension !== null,
    refetchInterval: extension !== null ? 2000 : false,
  });

  // Task 5.1, brief step 4: read the SAME cache key WarmTransferPanel already
  // polls every 2s (mounted inside AgentConsole below) — no refetchInterval of
  // our own here, just a second subscriber, so this never doubles the poll
  // rate. Used only to swap the disposition header's copy while a transfer is
  // in the three-way conference; the panel itself is unaffected.
  const { data: warmTransfer } = useQuery({
    queryKey: ["warm-transfer"],
    // Same seat the console/panel pass (beads u0np) — a second subscriber to
    // the shared key must resolve the seat the same way, or an operator on the
    // shared login 422s this poll.
    queryFn: () => getWarmTransfer(extension ?? undefined),
    enabled: !!call,
  });

  // Wrap-up target: the call to disposition. /current-call reports the seat's
  // LIVE bridge, so it goes null the instant the customer hangs up — and the
  // normal flow is customer-hangs-up-then-agent-wraps-up, which would leave the
  // agent with nothing to click. It also reads null mid-warm-transfer (the
  // consult leg carries no spool id, and a merged conference is three-party).
  // So hold the last call we saw until it has actually been dispositioned.
  //
  // Only an ANSWERED campaign call is ever a wrap-up target. The pop now
  // fires while the seat is still ringing (F12) and for calls with no spool
  // behind them (inbound / manual); a call the agent declined or never took,
  // or one with nothing to disposition, must not linger as "wrap up the last
  // call".
  const [wrapUp, setWrapUp] = useState<CurrentCall | null>(null);
  useEffect(() => {
    if (call && call.answered && call.spool_id != null) setWrapUp(call);
  }, [call]);
  const disposeTarget = call && call.answered && call.spool_id != null ? call : wrapUp;
  const disposeSpoolId = disposeTarget?.spool_id ?? null;

  // F6 lead capture. Shown from the moment a CAMPAIGN call pops (while still
  // ringing, so a colleague's earlier notes are on screen before "hello") and
  // held, like wrap-up, until the call is dispositioned — the customer
  // hanging up must not take the form away while the rep is still typing.
  // Keyed by spool_id below so each call gets its own state.
  const leadTarget = call && call.spool_id != null ? call : wrapUp;

  // Who is typing, on which seat. On-device lead drafts are keyed by this so
  // a draft one rep left unsent on a shared floor PC is never shown to, or
  // replayed under the login of, the next rep to sit down — and so an
  // operator seat's drafts follow that seat. For an agent the seat comes with
  // `me`, so the scope is known exactly when the seat is.
  const draftScope =
    extension === null
      ? null
      : isAgent
        ? me
          ? `agent:${me.agent_user_id}:${extension}`
          : null
        : `operator:${user?.user_id ?? "?"}:${extension}`;

  // Anything the last session typed but never got acknowledged is replayed
  // once, as soon as we know whose drafts to replay, before any new call
  // lands. (The 2s poll re-renders do not change the scope, so this does not
  // re-run on them.)
  useEffect(() => {
    if (draftScope) void flushLeadDrafts(draftScope);
  }, [draftScope]);

  const dispose = useMutation({
    mutationFn: ({ spoolId, disp }: { spoolId: number; disp: string }) =>
      submitDisposition(spoolId, disp),
    onSuccess: (_d, vars) => {
      setLastDisposed(vars.disp);
      setWrapUp(null);   // wrapped: stop offering this call
      qc.invalidateQueries({ queryKey: ["agent-current-call", extension] });
    },
  });

  // --- Operator, not yet on a seat: keep the extension entry box (backward
  // compatibility — this is the shared-login flow operators use today, and
  // removing it would break every agent sharing an operator's own login). ---
  if (!isAgent && extension === null) {
    return (
      <div className="max-w-sm mx-auto mt-10 bg-white rounded-lg shadow p-8 text-center space-y-4">
        <h1 className="text-2xl font-bold">Agent Panel</h1>
        <p className="text-sm text-gray-500">Enter your extension to start taking calls.</p>
        <input
          className="w-full border border-gray-300 rounded px-3 py-2 text-center text-lg"
          placeholder="Extension ID"
          value={extInput}
          onChange={(e) => setExtInput(e.target.value)}
        />
        <button
          onClick={() => extInput && setOperatorExtension(Number(extInput))}
          className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2"
        >
          Go on shift
        </button>
      </div>
    );
  }

  // --- Agent principal whose seat hasn't resolved yet (GET /api/agent/me
  // still in flight). Briefly true on first load only. ---
  if (extension === null) {
    return (
      <div className="max-w-sm mx-auto mt-10 text-center text-sm text-gray-500">
        Resolving your seat…
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* The console owns state reporting outright — the header carries no
          second status indicator to contradict it (the old always-green "On
          shift" dot stayed green even with the softphone offline). */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Agent console</h1>
        {/* "End shift" only makes sense for the operator's shared-login flow —
            it releases the typed-in extension so a colleague can use the same
            login. An agent's seat is fixed for the session; they sign out of
            the whole console instead, from the header above (AgentLayout). */}
        {!isAgent && (
          <button
            onClick={() => setOperatorExtension(null)}
            className="px-3 py-1.5 rounded text-sm bg-gray-100 hover:bg-gray-200"
          >
            End shift
          </button>
        )}
      </div>

      <AgentConsole
        extensionId={extension}
        call={call ?? null}
        onPhoneChange={handlePhoneChange}
        testCallActive={testCallActive}
      />

      {leadTarget && leadTarget.spool_id != null && extension !== null && draftScope && (
        <LeadCapturePanel
          key={leadTarget.spool_id}
          spoolId={leadTarget.spool_id}
          phone={leadTarget.phone}
          extensionId={extension}
          draftScope={draftScope}
          ended={!call}
        />
      )}

      {/* Wrap-up only appears when there is something to wrap up. Caller
          identity lives in the console above, so it isn't repeated here. */}
      {disposeTarget ? (
        <div className="bg-white rounded-2xl shadow overflow-hidden">
          <div className="px-6 pt-5 pb-6">
            <div className="text-xs uppercase text-gray-400 font-semibold mb-3">
              {warmTransfer?.state === "conference"
                ? "Disposition — wrap up before you leave the call"
                : !call
                  ? "Disposition — wrap up the last call"
                  : "Disposition — click to wrap up"}
            </div>
            {/* An empty grid used to be indistinguishable from a failed fetch —
                the agent just saw a heading and no buttons, with nothing saying
                why (beads pz2z). Say it out loud instead. */}
            {dispositionsFailed && (
              <div className="bg-red-50 text-red-800 rounded px-3 py-2 text-sm mb-2">
                Could not load wrap-up options. Reload the page; if it persists,
                tell your supervisor — the call is still recorded.
              </div>
            )}
            {!dispositionsFailed && (dispositions ?? []).length === 0 && (
              <div className="bg-amber-50 text-amber-800 rounded px-3 py-2 text-sm mb-2">
                No wrap-up options are set up yet.
              </div>
            )}
            {/* A failed save used to be silent: onSuccess never fired (so this
                wrap-up stayed on screen, unexplained) and nothing was shown.
                The usual cause is a session that timed out mid-call — the agent
                token expires with no refresh (beads s7wp). Say it, and make
                clear the call is kept here to retry after signing back in; the
                save itself is retained because the wrap-up surface only clears
                on success. */}
            {dispose.isError && (
              <div className="bg-red-50 text-red-800 rounded px-3 py-2 text-sm mb-2">
                Couldn't save that wrap-up — your session may have timed out.
                Sign in again; this call is kept here so you can wrap it up.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(dispositions ?? []).map((d) => (
                <button
                  key={d.disposition_id}
                  disabled={dispose.isPending}
                  onClick={() => {
                    if (disposeSpoolId != null) dispose.mutate({ spoolId: disposeSpoolId, disp: d.name });
                  }}
                  title={d.description ?? undefined}
                  className="border rounded-lg px-3 py-3 text-sm font-medium hover:bg-brand hover:text-white hover:border-brand transition disabled:opacity-50"
                >
                  {d.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        lastDisposed && (
          <p className="text-center text-sm text-gray-500">
            Last call wrapped: <strong className="text-gray-700">{lastDisposed}</strong>
          </p>
        )
      )}

      {/* Below wrap-up rather than above it (as it was): during a call the
          rep's eyes go console -> lead details -> disposition, and the
          self-test is an idle-time tool. */}
      <div className="mt-4">
        <ConnectionTest
          extensionId={extension}
          liveSoftphone={liveSoftphone}
          livePhoneState={livePhoneState}
          livePhoneStateSeq={livePhoneStateSeq}
          onTestCallActiveChange={setTestCallActive}
        />
      </div>
    </div>
  );
}
